/**
 * Organization Settings Component (TASK-051, TASK-135)
 *
 * Form for managing organization settings:
 * - Organization name
 * - Organization slug (read-only)
 * - Logo upload
 * - Bot settings (company description for operator mode)
 * - Subscription info
 * - Usage limits
 */
import { useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import { InfoTooltip } from '@/components/ui/info-tooltip';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type {
  OrganizationSettings as OrgSettingsType,
  OrganizationMode,
  EcommerceSettings,
} from '@/types/settings';
import { modeLabels } from '@/lib/modeLabels';
import { OrganizationContacts } from './OrganizationContacts';
import { LeadAttributesEditor } from './LeadAttributesEditor';
import { ManagerVisibilitySetting } from './ManagerVisibilitySetting';

/** Default catalog summary threshold (products) shown when unset (TASK-270) */
const DEFAULT_CATALOG_THRESHOLD = 40;
/** Default catalog currency shown when unset (TASK-270) */
const DEFAULT_ECOMMERCE_CURRENCY = 'RUB';

interface OrganizationSettingsProps {
  settings: OrgSettingsType;
  onChange: (updates: Partial<OrgSettingsType>) => void;
  onSave: () => void;
  onLogoUpload?: (file: File) => void;
  logoUploading?: boolean;
  disabled: boolean;
  nameError: string | null;
}

/**
 * Organization Settings Form Component
 */
export function OrganizationSettings({
  settings,
  onChange,
  onSave,
  onLogoUpload,
  logoUploading = false,
  disabled,
  nameError,
}: OrganizationSettingsProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  // TASK-270: switching cabinet mode is a heavy operation (it changes which
  // sources are allowed and how the AI assistant behaves), so we gate it behind
  // a confirmation dialog instead of applying the toggle immediately.
  const [pendingMode, setPendingMode] = useState<OrganizationMode | null>(null);

  const isEcommerce = settings?.mode === 'ecommerce';
  const ecommerce: EcommerceSettings = settings?.ecommerce_settings || {};

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ name: e.target.value });
  };

  // Ask for confirmation before flipping the mode; apply it only on confirm.
  const handleModeToggle = (checked: boolean) => {
    setPendingMode(checked ? 'ecommerce' : 'estimator');
  };

  const confirmModeChange = () => {
    if (pendingMode) {
      onChange({ mode: pendingMode });
    }
    setPendingMode(null);
  };

  const cancelModeChange = () => {
    setPendingMode(null);
  };

  // Patch a single e-commerce setting field, preserving the rest.
  const patchEcommerce = (updates: Partial<EcommerceSettings>) => {
    onChange({ ecommerce_settings: { ...ecommerce, ...updates } });
  };

  // Parse a numeric input, treating an empty string as "unset" (undefined).
  const parseNumber = (raw: string): number | undefined => {
    if (raw.trim() === '') {
      return undefined;
    }
    const value = Number(raw);
    return Number.isNaN(value) ? undefined : value;
  };

  const handleCompanyNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ company_name: e.target.value });
  };

  const handleCompanyDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange({ company_description: e.target.value });
  };

  const handleLogoClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onLogoUpload) {
      onLogoUpload(file);
    }
    e.target.value = '';
  };

  return (
    <div data-testid="organization-settings" className="space-y-6">
      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Основная информация</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Organization Name */}
          <div className="space-y-2">
            <Label htmlFor="org-name">Название организации</Label>
            <Input
              id="org-name"
              data-testid="org-name-input"
              value={settings?.name || ''}
              onChange={handleNameChange}
              disabled={disabled}
              placeholder="Введите название организации"
            />
            {nameError && (
              <p data-testid="org-name-error" className="text-sm text-red-600">
                {nameError}
              </p>
            )}
          </div>

          {/* Organization Slug */}
          <div className="space-y-2">
            <div className="flex items-center gap-1">
              <Label htmlFor="org-slug">Идентификатор организации</Label>
              <InfoTooltip text="Уникальный идентификатор вашей организации. Не может быть изменён." />
            </div>
            <Input
              id="org-slug"
              data-testid="org-slug-input"
              value={settings?.slug || ''}
              readOnly
              disabled
              className="bg-gray-50"
            />
          </div>

          {/* Logo Upload */}
          <div className="space-y-2">
            <Label>Логотип организации</Label>
            <div data-testid="org-logo-upload" className="flex items-center gap-4">
              {settings?.logo_url ? (
                <img
                  src={settings.logo_url}
                  alt="Логотип организации"
                  className="w-16 h-16 rounded-lg object-cover border"
                />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center border">
                  <Upload className="w-6 h-6 text-gray-400" />
                </div>
              )}
              <div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleLogoClick}
                  disabled={disabled || logoUploading}>
                  {logoUploading ? 'Загрузка...' : 'Загрузить логотип'}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={disabled || logoUploading}
                  onChange={handleFileChange}
                />
                <p className="text-sm text-gray-500 mt-1">PNG, JPG до 2 МБ</p>
              </div>
            </div>
          </div>

          {/* Company Name (spoken by the bot in greetings) */}
          <div className="space-y-2">
            <div className="flex items-center gap-1">
              <Label htmlFor="company-name">Название компании</Label>
              <InfoTooltip text="Название вашей компании. Бот произносит его в приветствии («Здравствуйте, компания …»)." />
            </div>
            <Input
              id="company-name"
              data-testid="company-name-input"
              value={settings?.company_name || ''}
              onChange={handleCompanyNameChange}
              disabled={disabled}
              placeholder="СтройМастер"
            />
          </div>

          {/* Company Description (TASK-207: moved out of "Настройки бота" card) */}
          <div className="space-y-2">
            <div className="flex items-center gap-1">
              <Label htmlFor="company-description">Описание компании</Label>
              <InfoTooltip text='Это описание будет использоваться в режиме "Оператор" для представления компании клиентам. Бот будет использовать эту информацию для представления и консультации клиентов.' />
            </div>
            <Textarea
              id="company-description"
              data-testid="company-description-input"
              value={settings?.company_description || ''}
              onChange={handleCompanyDescriptionChange}
              disabled={disabled}
              placeholder="Опыт, гарантии, сертификаты — бот расскажет когда клиент спросит"
              rows={4}
            />
          </div>

          {/* KAN-742: organization contacts (go into the bot prompt) */}
          <OrganizationContacts settings={settings} onChange={onChange} disabled={disabled} />
        </CardContent>
      </Card>

      {/* KAN-783: what the bot finds out about the client (lead attributes) */}
      <Card data-testid="lead-attributes-settings">
        <CardHeader>
          <CardTitle className="text-lg">Что бот выясняет у клиента</CardTitle>
        </CardHeader>
        <CardContent>
          <LeadAttributesEditor
            attributes={settings?.lead_attributes ?? []}
            onChange={(lead_attributes) => onChange({ lead_attributes })}
            reservedNames={settings?.lead_attribute_reserved_names}
            disabled={disabled}
          />
        </CardContent>
      </Card>

      {/* KAN-811: which clients a manager with a cabinet login sees */}
      <ManagerVisibilitySetting
        value={settings?.manager_visibility}
        onChange={(manager_visibility) => onChange({ manager_visibility })}
        disabled={disabled}
      />

      {/* E-commerce Module (TASK-270) */}
      <Card data-testid="ecommerce-settings">
        <CardHeader>
          <CardTitle className="text-lg">Модуль интернет-магазин</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Mode toggle */}
          <div className="space-y-1">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="ecommerce-mode-toggle"
                data-testid="ecommerce-mode-toggle"
                checked={isEcommerce}
                onCheckedChange={(checked) => handleModeToggle(checked === true)}
                disabled={disabled}
              />
              <Label htmlFor="ecommerce-mode-toggle" className="cursor-pointer">
                Модуль интернет-магазин
              </Label>
            </div>
            <p className="text-xs text-gray-500 ml-6">
              Включает режим интернет-магазина: каталог товаров и фид-источники
              вместо списка услуг.
            </p>
            <p className="text-xs text-gray-500 ml-6">
              Текущий режим:{' '}
              <span data-testid="organization-mode-name" className="font-medium text-gray-700">
                {modeLabels(settings?.mode).modeName}
              </span>
            </p>
          </div>

          {/* E-commerce knobs — visible only when the module is enabled */}
          {isEcommerce && (
            <div data-testid="ecommerce-fields" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="ecommerce-delivery-cost">Стоимость доставки</Label>
                <Input
                  id="ecommerce-delivery-cost"
                  data-testid="ecommerce-delivery-cost-input"
                  type="number"
                  value={ecommerce.delivery_cost ?? ''}
                  onChange={(e) =>
                    patchEcommerce({ delivery_cost: parseNumber(e.target.value) })
                  }
                  disabled={disabled}
                  placeholder="0"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ecommerce-markup-percent">Наценка, %</Label>
                <Input
                  id="ecommerce-markup-percent"
                  data-testid="ecommerce-markup-percent-input"
                  type="number"
                  value={ecommerce.markup_percent ?? ''}
                  onChange={(e) =>
                    patchEcommerce({ markup_percent: parseNumber(e.target.value) })
                  }
                  disabled={disabled}
                  placeholder="0"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ecommerce-currency">Валюта</Label>
                <Input
                  id="ecommerce-currency"
                  data-testid="ecommerce-currency-input"
                  value={ecommerce.currency ?? DEFAULT_ECOMMERCE_CURRENCY}
                  onChange={(e) => patchEcommerce({ currency: e.target.value })}
                  disabled={disabled}
                  placeholder={DEFAULT_ECOMMERCE_CURRENCY}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-1">
                  <Label htmlFor="ecommerce-catalog-threshold">
                    Порог каталога в промпте
                  </Label>
                  <InfoTooltip text="Если товаров больше этого числа — каталог подаётся в промпт ассистента в свёрнутом виде." />
                </div>
                <Input
                  id="ecommerce-catalog-threshold"
                  data-testid="ecommerce-catalog-threshold-input"
                  type="number"
                  value={ecommerce.catalog_threshold ?? DEFAULT_CATALOG_THRESHOLD}
                  onChange={(e) =>
                    patchEcommerce({
                      catalog_threshold: parseNumber(e.target.value),
                    })
                  }
                  disabled={disabled}
                  placeholder={String(DEFAULT_CATALOG_THRESHOLD)}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mode-change confirmation (TASK-270) */}
      <AlertDialog
        open={pendingMode !== null}
        onOpenChange={(open) => {
          if (!open) cancelModeChange();
        }}
      >
        <AlertDialogContent data-testid="mode-change-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Сменить режим кабинета?</AlertDialogTitle>
            <AlertDialogDescription>
              Смена режима повлияет на источники и работу AI-ассистента.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="mode-change-cancel" onClick={cancelModeChange}>
              Отмена
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="mode-change-confirm"
              onClick={confirmModeChange}
            >
              Сменить режим
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* KAN-1000: блок "Подписка" убран: тарифов нет, деньги в "Баланс и расход". */}

      {/* TASK-299: скрыто до утверждения ценовой/подписочной политики */}
      {false && (
        <Card data-testid="usage-limits">
          <CardHeader>
            <CardTitle className="text-lg">Лимиты использования</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold" data-testid="limit-max-users">
                  {settings?.max_users || 0}
                </p>
                <p className="text-sm text-gray-500">Макс. пользователей</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold" data-testid="limit-max-estimates">
                  {settings?.max_estimates_per_month || 0}
                </p>
                <p className="text-sm text-gray-500">Смет в месяц</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold" data-testid="limit-max-data-sources">
                  {settings?.max_data_sources || 0}
                </p>
                <p className="text-sm text-gray-500">Источников данных</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold">
                  {settings?.max_storage_mb ? `${(settings.max_storage_mb / 1024).toFixed(1)} GB` : '0 GB'}
                </p>
                <p className="text-sm text-gray-500">Хранилище</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Save Button */}
      <div className="flex justify-end">
        <Button data-testid="save-organization-button" onClick={onSave} disabled={disabled}>
          Сохранить настройки организации
        </Button>
      </div>
    </div>
  );
}
